import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import CodeWindow, { KW, STR, COM } from './CodeWindow';

type FlowId = 'magic' | 'oauth' | 'mfa' | 'realtime';

const flows: { id: FlowId; label: string; filename: string; body: JSX.Element }[] = [
  {
    id: 'magic',
    label: 'Magic link',
    filename: 'auth/sign-in.ts',
    body: (
      <pre className="whitespace-pre">
        <KW>import</KW> {'{ Pryma } '}
        <KW>from</KW> <STR>'pryma'</STR>
        {'\n\n'}
        <KW>const</KW> pryma = <KW>new</KW> Pryma({'({ projectId: '}
        <STR>'proj_a5n'</STR>
        {' })'}
        {'\n\n'}
        <COM>// Sign in with a magic link</COM>
        {'\n'}
        <KW>const</KW> {'{ user, session } = '}
        <KW>await</KW> pryma.auth.signIn({'({'}
        {'\n  method: '}
        <STR>'magic_link'</STR>,{'\n  email: '}
        <STR>'kira@stellar.dev'</STR>,{'\n})'}
        {'\n\n'}
        <COM>// Same SDK, every primitive.</COM>
        {'\n'}
        <KW>await</KW> pryma.realtime.broadcast(<STR>'users.signed_in'</STR>, user)
      </pre>
    ),
  },
  {
    id: 'oauth',
    label: 'OAuth',
    filename: 'auth/oauth.ts',
    body: (
      <pre className="whitespace-pre">
        <COM>// Redirect to GitHub, then exchange the code.</COM>
        {'\n'}
        <KW>const</KW> url = <KW>await</KW> pryma.auth.oauth.start({'({'}
        {'\n  provider: '}
        <STR>'github'</STR>,{'\n  redirectTo: '}
        <STR>'/dashboard'</STR>,{'\n})'}
        {'\n\n'}
        <COM>// In your /callback route</COM>
        {'\n'}
        <KW>const</KW> {'{ user, session } = '}
        <KW>await</KW> pryma.auth.oauth.callback({'({'}
        {'\n  code, state,\n})'}
        {'\n\n'}
        <COM>// Already linked? Returns the same user.</COM>
        {'\n'}
        <KW>return</KW> user
      </pre>
    ),
  },
  {
    id: 'mfa',
    label: 'MFA challenge',
    filename: 'auth/mfa.ts',
    body: (
      <pre className="whitespace-pre">
        <COM>// Step up after sign-in based on risk score</COM>
        {'\n'}
        <KW>const</KW> challenge = <KW>await</KW> pryma.auth.mfa.challenge({'({'}
        {'\n  userId: user.id,\n  factor: '}
        <STR>'totp'</STR>,{'\n})'}
        {'\n\n'}
        <COM>// Verify the user's 6-digit code</COM>
        {'\n'}
        <KW>const</KW> {'{ session } = '}
        <KW>await</KW> pryma.auth.mfa.verify({'({'}
        {'\n  challengeId: challenge.id,\n  code: req.body.code,\n})'}
        {'\n\n'}
        <COM>// Recovery codes auto-rotated.</COM>
      </pre>
    ),
  },
  {
    id: 'realtime',
    label: 'Realtime',
    filename: 'realtime/sync.ts',
    body: (
      <pre className="whitespace-pre">
        <COM>// Auth-aware presence — no separate token plumbing</COM>
        {'\n'}
        <KW>const</KW> ch = pryma.realtime.channel(<STR>'project:a5n'</STR>, {'{ user })'}
        {'\n\n'}
        ch.on(<STR>'cursor.move'</STR>, {'(e) => '}
        {'{'}
        {'\n  applyCursor(e.userId, e.x, e.y)\n})'}
        {'\n\n'}
        <KW>await</KW> ch.broadcast(<STR>'cursor.move'</STR>, {'{ x, y })'}
      </pre>
    ),
  },
];

const ROTATE_MS = 5000;

export default function HeroCodeCycler() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setActiveIdx(i => (i + 1) % flows.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [paused]);

  const flow = flows[activeIdx];

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="space-y-3"
    >
      {/* tab pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        {flows.map((f, i) => {
          const active = i === activeIdx;
          return (
            <button
              key={f.id}
              onClick={() => {
                setActiveIdx(i);
                setPaused(true);
              }}
              className={`relative overflow-hidden rounded-full border px-3 py-1 text-[11px] font-semibold transition-all duration-200 ${
                active
                  ? 'border-violet bg-violetSoft text-violet'
                  : 'border-border bg-surface text-body hover:border-borderStrong hover:text-ink'
              }`}
            >
              {f.label}
              {active && !paused && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-left bg-pryma-grad"
                  style={{
                    animation: `progress ${ROTATE_MS}ms linear forwards`,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      <CodeWindow key={flow.id} filename={flow.filename} edge className="animate-fade-in">
        {flow.body}
      </CodeWindow>

      <style>{`
        @keyframes progress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}
